/**
 * Entity-context prompt budget — defense-in-depth caps for the TIER-1 entity
 * block in the system prompt.
 *
 * The host app (Hub) filters `entityData` through a per-entity-type allowlist
 * BEFORE it reaches this package (see apps/hub entity-context-formatter). But
 * this package is also consumed by non-hub callers, so the dump site itself
 * enforces hard budgets:
 *
 * - Per-field cap  (`MERIDIAN_ENTITY_FIELD_MAX_CHARS`, default 500): any
 *   formatted value longer than the cap is truncated with `…[truncated]`.
 *   Nested objects are JSON.stringify'd FIRST, then capped.
 * - Per-entity cap (`MERIDIAN_ENTITY_CONTEXT_MAX_CHARS`, default 4000): once
 *   the accumulated field lines would exceed the cap, remaining fields are
 *   omitted and a `…additional fields omitted (N)` note tells the model.
 *
 * Deliberately shaped as ONE-entity-in → formatted-block-out so a future
 * multi-entity caller can map over an array of entities without reshaping.
 */

export const DEFAULT_ENTITY_FIELD_MAX_CHARS = 500;
export const DEFAULT_ENTITY_CONTEXT_MAX_CHARS = 4000;

export const ENTITY_FIELD_TRUNCATION_MARKER = '…[truncated]';

export interface EntityContextCaps {
  /** Max characters for a single formatted field value. */
  fieldMaxChars: number;
  /** Max total characters for the entity's formatted field lines. */
  totalMaxChars: number;
}

export interface EntityDataFormatResult {
  /** The formatted `  - Key: value` lines (plus omission note), newline-joined. */
  text: string;
  /** Fields whose value was truncated at the per-field cap. */
  truncatedFieldCount: number;
  /** Fields dropped entirely because the per-entity budget was exhausted. */
  omittedFieldCount: number;
  /** Approximate characters removed by truncation + omission. */
  charsSaved: number;
}

function parsePositiveInt(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** Resolve caps from env (`MERIDIAN_ENTITY_FIELD_MAX_CHARS` / `MERIDIAN_ENTITY_CONTEXT_MAX_CHARS`), falling back to defaults. */
export function resolveEntityContextCaps(): EntityContextCaps {
  return {
    fieldMaxChars: parsePositiveInt(
      process.env.MERIDIAN_ENTITY_FIELD_MAX_CHARS,
      DEFAULT_ENTITY_FIELD_MAX_CHARS
    ),
    totalMaxChars: parsePositiveInt(
      process.env.MERIDIAN_ENTITY_CONTEXT_MAX_CHARS,
      DEFAULT_ENTITY_CONTEXT_MAX_CHARS
    ),
  };
}

/** snake_case → Title Case (unchanged from the original dump site). */
function formatFieldKey(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Format a field value the way the original dump site did: arrays join with
 * `, `, nested objects JSON.stringify, everything else String-coerced.
 */
function formatFieldValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'object' && value !== null) {
    try {
      const serialized = JSON.stringify(value);
      return typeof serialized === 'string' ? serialized : '[unserializable object]';
    } catch {
      // Circular reference etc. — cannot happen for header-parsed JSON, but
      // non-hub callers may pass arbitrary objects.
      return '[unserializable object]';
    }
  }
  return String(value);
}

/**
 * Format ONE entity's data into budget-capped `  - Key: value` lines.
 *
 * Non-null fields are emitted in insertion order. Each value is formatted
 * (array join / object stringify / String), truncated at the per-field cap,
 * and accumulated until the per-entity cap would be exceeded — remaining
 * fields are omitted with a note so the model knows the list is partial.
 */
export function formatEntityDataForPrompt(
  entityData: Record<string, unknown>,
  caps: EntityContextCaps = resolveEntityContextCaps()
): EntityDataFormatResult {
  const lines: string[] = [];
  let truncatedFieldCount = 0;
  let omittedFieldCount = 0;
  let charsSaved = 0;
  let totalChars = 0;
  let budgetExhausted = false;

  for (const [key, value] of Object.entries(entityData)) {
    if (value === null || value === undefined) continue;

    const formattedKey = formatFieldKey(key);
    const fullValue = formatFieldValue(value);

    let formattedValue = fullValue;
    let truncatedThisField = false;
    if (fullValue.length > caps.fieldMaxChars) {
      formattedValue = `${fullValue.slice(0, caps.fieldMaxChars)}${ENTITY_FIELD_TRUNCATION_MARKER}`;
      truncatedThisField = true;
    }

    const line = `  - ${formattedKey}: ${formattedValue}`;

    if (budgetExhausted || totalChars + line.length + 1 > caps.totalMaxChars) {
      budgetExhausted = true;
      omittedFieldCount += 1;
      charsSaved += line.length;
      continue;
    }

    if (truncatedThisField) {
      truncatedFieldCount += 1;
      charsSaved += fullValue.length - formattedValue.length;
    }

    lines.push(line);
    totalChars += line.length + 1; // +1 for the joining newline
  }

  if (omittedFieldCount > 0) {
    lines.push(`  - …additional fields omitted (${omittedFieldCount})`);
  }

  return {
    text: lines.join('\n'),
    truncatedFieldCount,
    omittedFieldCount,
    charsSaved,
  };
}
